import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Assert } from '@cotera/utilities';
import { useUntenantedClient } from '@cotera/client/app/stores/org';
import { TimeInterval } from '@cotera/api';
import { SummaryCards } from '../summary-cards';
import { SagaWorkflowsTable } from '../saga-workflows-table';
import { FailureTypesTable } from '../failure-types-table';
import { buildSagaFailureRows } from '../utils/saga-failure-rows';
import { atcQueryKeys } from '../query-keys';

interface GroupedSnapshotTabProps {
  timeInterval: TimeInterval;
}

export const GroupedSnapshotTab: React.FC<GroupedSnapshotTabProps> = ({
  timeInterval,
}) => {
  const client = useUntenantedClient();

  const { data: sagaData } = useQuery({
    queryFn: async () => {
      const res = await client.atcVisibility.getSagaInvocations({
        timeInterval,
        includeSagaType: 'GroupedSnapshot',
      });
      return Assert.assertOk(res);
    },
    queryKey: atcQueryKeys.sagaInvocations(timeInterval, {
      include: 'GroupedSnapshot',
    }),
  });

  const filtered = (sagaData ?? []).filter(
    (inv) => inv.sagaType === 'GroupedSnapshot'
  );
  const totalSuccess = filtered.filter(
    (inv) => inv.completionResult === 'success'
  ).length;
  const totalFailures = filtered.filter(
    (inv) => inv.completionResult === 'failure'
  ).length;
  const totalCompleted = totalSuccess + totalFailures;

  const failureRows = buildSagaFailureRows(filtered);

  return (
    <div className="space-y-6">
      <SummaryCards
        totalJobs={filtered.length}
        successRate={
          totalCompleted > 0 ? (totalSuccess / totalCompleted) * 100 : 0
        }
        totalFailures={totalFailures}
      />

      <SagaWorkflowsTable
        timeInterval={timeInterval}
        sagaTypeFilter={{ include: 'GroupedSnapshot' }}
      />

      <FailureTypesTable data={failureRows} />
    </div>
  );
};
